import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useState } from "react";
import { markTourSeen } from "../../lib/api/index.js";
import { queryKeys } from "../../lib/query-keys.js";
import { PATHS } from "../../lib/routes.js";
import { navigate, withReport } from "../../lib/url-filters.js";
import { useToast } from "../toast.js";
import { TOUR_STEPS } from "./tour-steps.js";

/**
 * The tour's state, owned by the signed-in layout (plans/help-info-and-tour.md).
 *
 * One level above the screens, because the tour outlives them: its last steps
 * open Prompts and Where to publish, which unmount the Overview that started it.
 * Local UI state, passed down by context like the workspace is — nothing an
 * address should carry, and nothing the server holds except "seen".
 */

export interface Tour {
  /** The step showing, or null when the tour is not running. */
  readonly at: number | null;
  readonly start: () => void;
  readonly go: (index: number) => void;
  /** Ends it, finished or not, and records that it was seen or declined. */
  readonly finish: (completed: boolean) => void;
}

export const TourContext = createContext<Tour | null>(null);

export function useTour(): Tour {
  const tour = useContext(TourContext);
  if (tour === null) throw new Error("useTour needs the signed-in layout above it");
  return tour;
}

/** Whether a step's screen is the one showing. Overview also lives at `/`. */
function isShowing(screen: keyof typeof PATHS): boolean {
  const here = window.location.pathname;
  return here === PATHS[screen] || (screen === "overview" && here === PATHS.home);
}

export function useTourController(reportId: string | null): Tour {
  const [at, setAt] = useState<number | null>(null);
  const client = useQueryClient();
  const toast = useToast();

  const go = (index: number): void => {
    const step = TOUR_STEPS[index];
    if (step === undefined) return;
    if (!isShowing(step.screen)) {
      const path = PATHS[step.screen];
      navigate(reportId === null ? path : withReport(path, reportId));
    }
    setAt(index);
  };

  const finish = (completed: boolean): void => {
    setAt(null);
    if (completed) {
      toast.done({
        title: "Tour finished",
        detail: "Replay it anytime from your account menu.",
      });
    }
    void markTourSeen()
      .then(async () => {
        await client.invalidateQueries({ queryKey: queryKeys.account() });
      })
      .catch(() => {
        toast.error(
          "Could not save that you have seen the tour, so it may be offered again.",
        );
      });
  };

  return {
    at,
    start: () => {
      go(0);
    },
    go,
    finish,
  };
}
